import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  X,
  ShieldCheck,
  Phone,
  Clock,
  Star,
} from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { howItWorks, services } from '../data/mock';
import SEOHead from '../components/seo/SEOHead';
import { BreadcrumbSchema } from '../components/seo/StructuredData';
import { PAGE_SEO } from '../data/routeSeo';

const HowItWorks = () => {
  const { hero, steps, numberOptions, comparison, included, notIncluded, faqs } =
    howItWorks;

  return (
    <Layout>
      <SEOHead
        {...PAGE_SEO['/how-it-works']}
        faqs={faqs}
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', path: '/' },
          { name: 'How It Works', path: '/how-it-works' },
        ]}
      />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6">
            {hero.badge}
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            {hero.title}
          </h1>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            {hero.subtitle}
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-8 py-6 rounded-xl"
            >
              Book a Free Discovery Call
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              From First Call to Live AI in 7–14 Days
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Four steps. You are involved in two of them, briefly.
            </p>
          </div>

          <div className="space-y-8">
            {steps.map((step) => (
              <div
                key={step.number}
                className="grid lg:grid-cols-12 gap-6 items-start bg-gray-50 rounded-3xl p-8 border border-gray-100"
              >
                <div className="lg:col-span-2">
                  <span className="text-5xl font-bold text-emerald-500/30">
                    {step.number}
                  </span>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                    <Clock className="w-4 h-4" />
                    {step.duration}
                  </div>
                </div>

                <div className="lg:col-span-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>

                <div className="lg:col-span-4 space-y-4">
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                      You do
                    </p>
                    <p className="text-sm text-gray-900 font-medium">
                      {step.youDo}
                    </p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                    <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-1">
                      We do
                    </p>
                    <p className="text-sm text-gray-900 font-medium">
                      {step.weDo}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Number options */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 text-amber-700 text-sm font-medium mb-6">
              <Phone className="w-4 h-4" />
              Choosing Your Number
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Which Number Should the AI Use?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {numberOptions.intro}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {numberOptions.options.map((option) => (
              <div
                key={option.title}
                className={`rounded-3xl p-8 bg-white border-2 ${
                  option.recommended
                    ? 'border-emerald-500 shadow-xl shadow-emerald-500/10'
                    : 'border-gray-200'
                }`}
              >
                {option.recommended && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-500 text-white text-xs font-semibold mb-4">
                    <Star className="w-3 h-3" />
                    Most Popular
                  </span>
                )}
                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                  {option.title}
                </h3>
                <p className="text-sm text-gray-500 mb-6">{option.forWho}</p>
                <ul className="space-y-3">
                  {option.points.map((point) => (
                    <li key={point} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                      <span className="text-gray-600">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Official vs unofficial */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium mb-6">
              <ShieldCheck className="w-4 h-4" />
              Why We Cost More Than the Cheapest Quote
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              We Only Build on the Official WhatsApp API
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {comparison.intro}
            </p>
          </div>

          <div className="overflow-x-auto rounded-3xl border border-gray-200">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-5 text-sm font-semibold text-gray-500 uppercase tracking-wide">
                    &nbsp;
                  </th>
                  <th className="p-5 text-sm font-semibold text-gray-500 uppercase tracking-wide">
                    Cheap Unofficial Vendors
                  </th>
                  <th className="p-5 text-sm font-semibold text-emerald-600 uppercase tracking-wide">
                    Dependify
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparison.rows.map((row, index) => (
                  <tr
                    key={row.point}
                    className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                  >
                    <td className="p-5 font-semibold text-gray-900 align-top">
                      {row.point}
                    </td>
                    <td className="p-5 text-gray-600 align-top">
                      <span className="flex items-start gap-2">
                        <X className="w-4 h-4 text-red-500 shrink-0 mt-1" />
                        {row.cheap}
                      </span>
                    </td>
                    <td className="p-5 text-gray-900 align-top">
                      <span className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-1" />
                        {row.us}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-6 text-sm text-gray-500 max-w-3xl mx-auto text-center leading-relaxed">
            {comparison.footnote}
          </p>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gray-900" id="pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6">
              Pricing
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Every Price, In One Place
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              A one-time setup fee, then a flat monthly fee. No per-seat
              charges, no lock-in contract, no surprises.
            </p>
          </div>

          <div className="overflow-x-auto rounded-3xl border border-white/10 bg-white/5 backdrop-blur">
            <table className="w-full min-w-[640px] text-left">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="p-5 text-sm font-semibold text-gray-400 uppercase tracking-wide">
                    Service
                  </th>
                  <th className="p-5 text-sm font-semibold text-gray-400 uppercase tracking-wide">
                    One-Time Setup
                  </th>
                  <th className="p-5 text-sm font-semibold text-gray-400 uppercase tracking-wide">
                    Monthly
                  </th>
                </tr>
              </thead>
              <tbody>
                {services.map((service) => (
                  <tr
                    key={service.id}
                    className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors"
                  >
                    <td className="p-5">
                      <Link
                        to={`/services/${service.id}`}
                        className="text-white font-medium hover:text-emerald-400 transition-colors"
                      >
                        {service.title}
                      </Link>
                    </td>
                    <td className="p-5 text-gray-400 whitespace-nowrap">
                      {service.setupFee
                        ? service.setupFee.replace('One-time setup: ', '')
                        : '—'}
                    </td>
                    <td className="p-5 text-emerald-400 font-semibold whitespace-nowrap">
                      {service.pricing.replace('Starting from ', '')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Included / not included */}
          <div className="grid md:grid-cols-2 gap-8 mt-12">
            <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
              <h3 className="text-xl font-bold text-white mb-6">
                {included.title}
              </h3>
              <ul className="space-y-3">
                {included.items.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
              <h3 className="text-xl font-bold text-white mb-2">
                {notIncluded.title}
              </h3>
              <p className="text-sm text-gray-400 mb-6 leading-relaxed">
                {notIncluded.intro}
              </p>
              <ul className="space-y-3">
                {notIncluded.items.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <ArrowRight className="w-4 h-4 text-amber-400 shrink-0 mt-1" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
            Questions We Get Asked
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div
                key={faq.question}
                className="bg-gray-50 rounded-2xl p-6 border border-gray-100"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Stop Losing Customers to Slow Replies?
          </h2>
          <p className="text-xl text-emerald-50 mb-8 leading-relaxed">
            Book a free 30-minute discovery call. We will map your enquiry flow,
            estimate your monthly message costs and tell you honestly whether
            this is right for your business.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-white hover:bg-gray-100 text-emerald-700 font-semibold px-8 py-6 text-lg rounded-xl"
            >
              Book Your Free Discovery Call
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default HowItWorks;
